import { createFileRoute, Link } from "@tanstack/react-router";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  Badge,
  Button,
  Card,
  Input,
  PageHeading,
  Stack,
  Table,
} from "~/components";
import { StageChip } from "~/components/intake/StageChip";
import { queryClient } from "~/lib/queryClient";
import { useDebouncedValue } from "~/lib/useDebouncedValue";
import {
  casesListQueryOptions,
  STAGES,
  useDeleteCase,
  type Case,
  type CaseListFilters,
  type Stage,
} from "~/lib/cases";
import { NULL_VALUE } from "~/lib/filters";

import * as css from "./_app.cases.index.css";

/** The filter every query on this page starts from: the case-management set. */
const LEDGER_FILTERS: CaseListFilters = { filevine_project_id_ne: NULL_VALUE };

// The firm's case-management ledger: every matter that has its Filevine
// project, whatever stage it is in. An intake matter is the Tower's until it is
// pushed, so the ledger never lists it (TMF-546).
//
// The row is the CASE ROW and nothing else (TMF-360): the list embeds no
// project, so there is no plan to read a phase off and no workflow name to
// show. Where a matter sits is its `stage` column, and a matter with none is
// the Filevine-synced one, which arrived already signed and never ran a
// funnel. A row names itself off the matter's own `name` column (TMF-512), so
// the list fetches nothing beside itself.
export const Route = createFileRoute("/_app/cases/")({
  component: CasesIndexPage,
  loader: () => queryClient.ensureQueryData(casesListQueryOptions(LEDGER_FILTERS)),
});

const ALL_STAGES = "__all__";

/** The chip row's selection: one stage, or every matter. */
type StageFilter = Stage | typeof ALL_STAGES;

/** What a cell shows for a value the matter does not carry. */
const EMPTY_VALUE = "\u2014";

/** How long the search box waits after the last keystroke before it asks. */
const SEARCH_DEBOUNCE_MS = 250;

function CasesIndexPage() {
  const deleteMutation = useDeleteCase();

  const [stageFilter, setStageFilter] = useState<StageFilter>(ALL_STAGES);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  // The name search is the API's, a case-insensitive contains match on the
  // matter's own name column, so the table narrows without the page holding
  // every matter to filter client-side.
  //
  // Only set a key when it should produce a URL param, so an empty search and
  // the "All" chip collapse to LEDGER_FILTERS, which collides on cache key with
  // the ledger query and dedupes at the network layer.
  const filters: CaseListFilters = useMemo(() => {
    const f: CaseListFilters = { ...LEDGER_FILTERS };
    const trimmed = debouncedSearch.trim();
    if (trimmed.length > 0) f.name = trimmed;
    if (stageFilter !== ALL_STAGES) f.stage = [stageFilter];
    return f;
  }, [debouncedSearch, stageFilter]);

  // Two queries:
  //   - ledgerQuery: the chip counts and the "no cases yet" empty state, over
  //     the whole ledger and nothing outside it.
  //   - filteredQuery: the table rows, with the previous result held visible
  //     during a refetch so the table does not blank out between chips.
  const [ledgerQuery, filteredQuery] = useQueries({
    queries: [
      casesListQueryOptions(LEDGER_FILTERS),
      {
        ...casesListQueryOptions(filters),
        placeholderData: keepPreviousData,
      },
    ],
  });

  const ledgerCases = ledgerQuery.data?._embedded ?? [];
  const filteredCases = filteredQuery.data?._embedded ?? [];

  // Counted off the whole ledger so the chip row stays full and the counts
  // do not collapse when one stage is selected.
  const stageCounts = useMemo(() => {
    const counts = new Map<Stage, number>();
    for (const c of ledgerCases) {
      if (c.stage === null) continue;
      counts.set(c.stage, (counts.get(c.stage) ?? 0) + 1);
    }
    return counts;
  }, [ledgerCases]);

  const handleDelete = (caseRow: Case) => {
    if (!confirm(`Delete the matter for "${caseRow.name || caseRow.id}"? This cannot be undone.`)) return;
    deleteMutation.mutate(caseRow.id);
  };

  const heading = (
    <PageHeading
      title="Cases"
      subtitle="Every matter in case management, whatever stage it is in."
    />
  );

  if (ledgerQuery.isPending) {
    return (
      <Stack gap="lg">
        {heading}
        <Card>
          <p>Loading cases…</p>
        </Card>
      </Stack>
    );
  }

  // Distinct empty states:
  //   - No matter is in case management yet → the Filevine project is what
  //     brings one in.
  //   - The ledger has matters but the active chip and search match none → in-table
  //     "no matches" message, chip row stays visible.
  if (ledgerCases.length === 0) {
    return (
      <Stack gap="lg">
        {heading}
        <Card>
          <Stack gap="md" align="center" className={css.emptyState}>
            <p>No cases yet. A matter joins case management once it has its Filevine project.</p>
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {heading}

      <StageChips
        stageCounts={stageCounts}
        totalCount={ledgerCases.length}
        active={stageFilter}
        onChange={setStageFilter}
      />

      <div className={css.filterRow}>
        <Input
          type="search"
          className={css.searchInput}
          placeholder="Search by client name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search cases by name"
        />
        <div className={css.counter}>
          Showing{" "}
          <span className={css.counterEmphasis}>{filteredCases.length}</span>{" "}
          of {ledgerCases.length} cases
        </div>
      </div>

      <Card>
        {filteredCases.length === 0 ? (
          <Stack gap="md" align="center" className={css.emptyState}>
            <p>No cases match the current filters.</p>
          </Stack>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Stage</th>
                <th>Source</th>
                <th>Days open</th>
                <th>Updated</th>
                <th className={css.actionsCell} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredCases.map((c) => (
                <CaseRow
                  key={c.id}
                  caseRow={c}
                  onDelete={() => handleDelete(c)}
                  isDeleting={deleteMutation.isPending && deleteMutation.variables === c.id}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}

function StageChips({
  stageCounts,
  totalCount,
  active,
  onChange,
}: {
  stageCounts: Map<Stage, number>;
  totalCount: number;
  active: StageFilter;
  onChange: (stage: StageFilter) => void;
}) {
  return (
    <div className={css.chipRow}>
      <button
        type="button"
        className={
          active === ALL_STAGES ? css.chip.activeAll : css.chip.inactive
        }
        onClick={() => onChange(ALL_STAGES)}
        aria-pressed={active === ALL_STAGES}
      >
        All <span className={css.chipCount}>({totalCount})</span>
      </button>
      {STAGES.map((stage) => {
        const isActive = active === stage;
        return (
          <button
            key={stage}
            type="button"
            className={isActive ? css.chip.activeStage : css.chip.inactive}
            // Toggle: clicking the active chip clears the filter.
            onClick={() => onChange(isActive ? ALL_STAGES : stage)}
            aria-pressed={isActive}
          >
            {stage} <span className={css.chipCount}>({stageCounts.get(stage) ?? 0})</span>
          </button>
        );
      })}
    </div>
  );
}

function CaseRow({
  caseRow,
  onDelete,
  isDeleting,
}: {
  caseRow: Case;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const updated = new Date(caseRow.updated_at).toLocaleDateString();
  const days = daysSince(caseRow.created_at);

  return (
    <tr>
      <td>
        <Link
          to="/cases/$id"
          params={{ id: caseRow.id }}
          className={css.caseName}
        >
          {caseRow.name || "Unnamed matter"}
        </Link>
      </td>
      <td>
        {/* A matter with no stage never ran a funnel: it arrived from Filevine
            already signed, which is what the badge says rather than leaving
            the cell blank. */}
        {caseRow.stage !== null ? (
          <StageChip name={caseRow.stage} />
        ) : (
          <Badge variant="neutral">Synced</Badge>
        )}
      </td>
      <td className={css.meta}>{caseRow.source ?? EMPTY_VALUE}</td>
      <td className={css.daysCell}>{days}d</td>
      <td className={css.meta}>{updated}</td>
      <td className={css.actionsCell}>
        <div className={css.actions}>
          <Link to="/cases/$id" params={{ id: caseRow.id }}>
            <Button type="button" variant="secondary">
              Open
            </Button>
          </Link>
          <Button
            type="button"
            variant="danger"
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </td>
    </tr>
  );
}

// Whole days between createdAt and now. Floors so a case opened a few
// hours ago reads as 0d, not 1d. The day count is informational, not
// SLA-bearing: the lifecycle engine owns deadlines.
function daysSince(createdAt: string): number {
  const ms = Date.now() - new Date(createdAt).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}
